/**
 * Colour conversions.
 *
 * The white-label properties of `DESIGN.md` §2 are written as **bare HSL channels** so Tailwind can
 * compose them (`hsl(var(--accent) / 0.08)`), while every fixed token is a hex. Converting between
 * the two lives here, so an accent can be measured (`contrast.ts`) and emitted as a custom property
 * from a single hex source of truth.
 */

/** `#RGB` or `#RRGGBB` to its three 0–255 channels. */
export function hexToRgb(hex: string): [number, number, number] {
  const value = hex.replace('#', '');
  const full =
    value.length === 3
      ? value
          .split('')
          .map((c) => c + c)
          .join('')
      : value;

  if (!/^[0-9a-fA-F]{6}$/.test(full)) {
    throw new Error(`Not a hex colour: ${hex}`);
  }

  return [
    parseInt(full.slice(0, 2), 16),
    parseInt(full.slice(2, 4), 16),
    parseInt(full.slice(4, 6), 16),
  ];
}

/**
 * A hex to the bare HSL channels a custom property carries: `#C2410C` → `17.47 88.35% 40.39%`.
 *
 * Two decimals, not zero: rounding `#C2410C` to `21 89% 41%` moves it to a visibly different
 * orange and drops it from 4.9:1 on paper to 4.5:1 — exactly the threshold, with no margin. The
 * hex is the decision; these channels are how it is written down.
 */
export function hexToHslChannels(hex: string): string {
  const [r, g, b] = hexToRgb(hex).map((channel) => channel / 255);
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;

  const lightness = (max + min) / 2;
  const saturation = delta === 0 ? 0 : delta / (1 - Math.abs(2 * lightness - 1));

  let hue = 0;
  if (delta !== 0) {
    if (max === r) {
      hue = (60 * ((((g - b) / delta) % 6) + 6)) % 360;
    } else if (max === g) {
      hue = 60 * ((b - r) / delta + 2);
    } else {
      hue = 60 * ((r - g) / delta + 4);
    }
  }

  const round = (value: number) => Number(value.toFixed(2));

  return `${round(hue)} ${round(saturation * 100)}% ${round(lightness * 100)}%`;
}

/** The inverse: `"17.47 88.35% 40.39%"` → `#C2410C`. */
export function hslChannelsToHex(channels: string): string {
  const match = channels.trim().match(/^(-?[\d.]+)\s+([\d.]+)%\s+([\d.]+)%$/);
  if (!match) {
    throw new Error(`Not HSL channels: ${channels}`);
  }

  const hue = Number(match[1]);
  const saturation = Number(match[2]) / 100;
  const lightness = Number(match[3]) / 100;

  const chroma = (1 - Math.abs(2 * lightness - 1)) * saturation;
  const secondary = chroma * (1 - Math.abs(((hue / 60) % 2) - 1));
  const offset = lightness - chroma / 2;

  const sextant = Math.floor(((((hue % 360) + 360) % 360) / 60) % 6);
  const rgbBySextant: Array<[number, number, number]> = [
    [chroma, secondary, 0],
    [secondary, chroma, 0],
    [0, chroma, secondary],
    [0, secondary, chroma],
    [secondary, 0, chroma],
    [chroma, 0, secondary],
  ];

  const hex = rgbBySextant[sextant]
    .map((channel) =>
      Math.round((channel + offset) * 255)
        .toString(16)
        .padStart(2, '0'),
    )
    .join('');

  return `#${hex.toUpperCase()}`;
}
